import type { RefObject } from 'react';
import { cx } from '../components/ui';
import { REACTION_META, totalReactionCount, totalsSummary } from '../lib/reactions';
import { REACTION_KINDS, type ReactionKind } from '../lib/types';
import type { ReactionsState } from './useReactions';

/**
 * The reaction bar, and the totals underneath it.
 *
 * It lives below the player rather than on top of it, for two reasons: a
 * control laid over the picture is a control that covers the picture, and a
 * button inside the video frame competes with the frame for focus. Tapping one
 * of these never pauses, never seeks, and never moves focus off the video.
 *
 * Every button says what it is in words as well as in glyph. The emoji is
 * `aria-hidden`, because "grinning face with smiling eyes" read aloud in the
 * middle of someone's design review helps nobody.
 */

function ReactionButton({
  kind,
  count,
  busy,
  justLanded,
  disabled,
  onClick,
}: {
  kind: ReactionKind;
  count: number;
  busy: boolean;
  justLanded: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const meta = REACTION_META[kind];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      // The player binds Space to play/pause on `window`. Without this, a
      // keyboard user who tabs to a reaction and presses Space pauses the
      // video instead of reacting.
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Spacebar') event.stopPropagation();
      }}
      aria-label={count > 0 ? `React with ${meta.label}, ${count} so far` : `React with ${meta.label}`}
      className={cx(
        'flex items-center gap-1.5 rounded border px-2.5 h-9 text-body transition-colors duration-120',
        'disabled:opacity-40 disabled:pointer-events-none',
        justLanded
          ? 'border-signal/50 bg-signal/10 text-chalk'
          : 'border-hairline bg-slate-900 text-chalk-dim hover:border-slate-600 hover:bg-slate-800 hover:text-chalk',
        busy && 'opacity-70',
      )}
    >
      <span aria-hidden="true" className="text-[15px] leading-none">
        {meta.glyph}
      </span>
      <span className="hidden sm:inline">{meta.label}</span>
      {count > 0 ? (
        <span aria-hidden="true" className="tnum text-caption text-chalk-faint">
          {count}
        </span>
      ) : null}
    </button>
  );
}

export function ReactionBar({
  state,
  mediaRef,
  ready,
}: {
  state: ReactionsState;
  /** Where "now" comes from. Read at click time, never subscribed to. */
  mediaRef: RefObject<HTMLMediaElement | null>;
  /** False while the recording is still processing: there is no "now" yet. */
  ready: boolean;
}) {
  // A share the viewer cannot see has no reactions to offer. Say nothing.
  if (state.unavailable) return null;

  const total = totalReactionCount(state.totals);
  const summary = totalsSummary(state.totals);

  const react = (kind: ReactionKind) => {
    // Reading `currentTime` at click time rather than from a re-rendering
    // state value keeps the bar out of the 250ms timeupdate render loop.
    state.react(kind, mediaRef.current?.currentTime ?? 0);
  };

  return (
    <section aria-labelledby="reactions-heading" className="space-y-2.5">
      <div className="flex items-baseline gap-2">
        <h2 id="reactions-heading" className="text-caption font-medium text-chalk-dim">
          Reactions
        </h2>
        {ready ? (
          <span className="text-caption text-chalk-faint">pinned to the moment you tap</span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-labelledby="reactions-heading">
        {REACTION_KINDS.map((kind) => (
          <ReactionButton
            key={kind}
            kind={kind}
            count={state.totals[kind]}
            busy={state.posting === kind}
            justLanded={state.lastAccepted === kind}
            disabled={!ready}
            onClick={() => react(kind)}
          />
        ))}
      </div>

      {/* The calm, permanent record. This is also the version that carries the
          information when motion is switched off and when nothing is animated
          at all, so it is a live region rather than plain text. */}
      <p role="status" className="min-h-[1.125rem] text-caption text-chalk-faint">
        {total > 0
          ? summary
          : ready
            ? 'No reactions yet. Tap one as you watch and it sticks to that moment.'
            : 'Reactions open once this recording has processed.'}
      </p>

      {state.postError ? (
        <p role="alert" className="flex items-start gap-1.5 text-caption text-danger">
          <span aria-hidden="true" className="mt-[0.4em] h-1 w-1 shrink-0 rounded-full bg-danger" />
          <span>{state.postError}</span>
        </p>
      ) : null}

      {state.loadError ? (
        <p className="text-caption text-chalk-faint">
          Existing reactions could not be loaded. {state.loadError}{' '}
          <button
            type="button"
            onClick={state.retry}
            className="text-signal underline underline-offset-2 hover:text-chalk"
          >
            Try again
          </button>
        </p>
      ) : null}
    </section>
  );
}
